import * as B from '@blueprintjs/core';
import React from 'react';

import {GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {UNITS_UI} from 'app/utils/mathUtils';

import {OverlayListSelector} from '../OverlayListSelector';
import cs from '../styles.module.scss';
import {AlertFormAction, AlertFormState} from '../useAlertForm';

type AreaLocationOption = 'default' | 'overlay';

export const AreaAlertStep: React.FC<{
  disableInputs: boolean;
  formState: AlertFormState;
  dispatch: React.Dispatch<AlertFormAction>;
  projectOverlays: Array<{id: number; name: string}>;
  organizationOverlays: Array<{id: number; name: string}>;
  overlaySettings?: Map<string, GeometryOverlaySetting>;
  displayAreaUnit: string;
}> = React.memo(
  ({
    formState,
    dispatch,
    disableInputs,
    projectOverlays,
    organizationOverlays,
    overlaySettings,
    displayAreaUnit,
  }) => {
    const [minAlertAreaInput, setMinAlertAreaInput] = React.useState<string>('');

    React.useEffect(() => {
      const valueM2 = formState.properties.minimumAlertingArea ?? 0;
      const displayValue = conversionUtils.convert(valueM2, C.UNIT_AREA_M2, displayAreaUnit);
      if (!Number.isFinite(displayValue)) {
        setMinAlertAreaInput('0');
        return;
      }
      if (!minAlertAreaInput.trim().endsWith('.')) {
        const rounded = Math.max(0, Math.round(displayValue * 100) / 100);
        setMinAlertAreaInput(rounded.toString());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState.properties.minimumAlertingArea, displayAreaUnit]);

    // A composite gates its combined mask the same way a threshold gates its own.
    const percentProps =
      formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD ||
      formState.alertType === C.CUSTOM_ALERT_TYPE_COMPOSITE
        ? formState.properties
        : undefined;

    const includeOverlays = formState.properties.includeOverlays ?? [];
    const excludeOverlays = formState.properties.excludeOverlays ?? [];
    const hasOverlayRestriction = includeOverlays.length > 0 || excludeOverlays.length > 0;
    // An overlay Upstream manages for the organization is neither on the portfolio nor in the
    // organization's library and can't be removed here, so the policy can't go back to monitoring
    // the whole property either.
    const knownOverlayIds = new Set(
      [...projectOverlays, ...organizationOverlays].map((overlay) => overlay.id)
    );
    const hasManagedOverlay = [...includeOverlays, ...excludeOverlays].some(
      (selection) => !knownOverlayIds.has(selection.featureCollectionId)
    );

    const [areaLocationOption, setAreaLocationOption] = React.useState<AreaLocationOption>(
      hasOverlayRestriction ? 'overlay' : 'default'
    );
    const [areaSizeOption, setAreaSizeOption] = React.useState<'default' | 'percentage' | 'area'>(
      formState.properties.minimumAlertingArea
        ? 'area'
        : percentProps?.percentOfProperty
          ? 'percentage'
          : 'default'
    );

    const hasOverlays = projectOverlays.length > 0;

    const chooseAreaLocation = (option: AreaLocationOption) => {
      setAreaLocationOption(option);
      // The worker applies every gate independently, so an option the wizard no longer shows
      // has to be cleared or it keeps filtering alerts.
      if (option === 'default') dispatch({type: 'CLEAR_OVERLAYS'});
      if (option === 'overlay' && areaSizeOption === 'percentage') {
        setAreaSizeOption('default');
        dispatch({type: 'SET_THRESHOLD_PERCENTAGE', value: 0});
      }
    };

    return (
      <div className={cs.dialogPanel}>
        <B.FormGroup label="Choose where to monitor for changes">
          {/* Each radio sits in a tooltip, which keeps the group from wiring it up, so the checked
              state and handler are set on the radios themselves. */}
          <B.RadioGroup
            selectedValue={areaLocationOption}
            onChange={({currentTarget: {value}}) => chooseAreaLocation(value as AreaLocationOption)}
            disabled={disableInputs}
          >
            <B.Radio
              label="Whole property"
              value="default"
              checked={areaLocationOption === 'default'}
              onChange={() => chooseAreaLocation('default')}
              disabled={hasManagedOverlay || disableInputs}
            />
            <B.Tooltip
              content="Add an overlay to your portfolio to use this option"
              disabled={hasOverlays || areaLocationOption === 'overlay' || disableInputs}
              position={B.Position.RIGHT}
            >
              <B.Radio
                label="Restrict by overlays"
                value="overlay"
                checked={areaLocationOption === 'overlay'}
                onChange={() => chooseAreaLocation('overlay')}
                disabled={(!hasOverlays && areaLocationOption !== 'overlay') || disableInputs}
              />
            </B.Tooltip>
          </B.RadioGroup>
        </B.FormGroup>

        {areaLocationOption === 'overlay' && !hasOverlays && !hasOverlayRestriction && (
          <B.Callout intent={B.Intent.WARNING} icon="warning-sign">
            This policy is configured to use an overlay restriction, but no overlays are currently
            available in this portfolio. Please add an overlay to your portfolio or switch to "Whole
            property" to resolve this issue.
          </B.Callout>
        )}

        {areaLocationOption === 'overlay' && (hasOverlays || hasOverlayRestriction) && (
          <OverlayListSelector
            projectOverlays={projectOverlays}
            organizationOverlays={organizationOverlays}
            overlaySettings={overlaySettings}
            includeOverlays={includeOverlays}
            excludeOverlays={excludeOverlays}
            dispatch={dispatch}
            disabled={disableInputs}
          />
        )}

        <B.FormGroup label="Choose a minimum size of change to monitor for">
          <B.RadioGroup
            selectedValue={areaSizeOption}
            onChange={({currentTarget: {value}}) => {
              const option = value as 'default' | 'percentage' | 'area';
              setAreaSizeOption(option);
              // The worker applies both gates independently, so the option the user left has to be cleared
              // or it keeps filtering alerts the wizard no longer shows.
              if (option !== 'percentage') dispatch({type: 'SET_THRESHOLD_PERCENTAGE', value: 0});
              if (option !== 'area') {
                setMinAlertAreaInput('');
                dispatch({type: 'SET_MINIMUM_ALERTING_AREA', value: null});
              }
            }}
            disabled={disableInputs}
          >
            <B.Radio value="default" label="Any size of change" />
            {percentProps && areaLocationOption !== 'overlay' && (
              <B.Radio value="percentage" label="Percent of property area" />
            )}
            <B.Radio value="area" label="Minimum area" />
          </B.RadioGroup>
        </B.FormGroup>

        {areaSizeOption === 'percentage' && percentProps && (
          <B.FormGroup label={`Set a minimum percent of property area for detected changes`}>
            <B.NumericInput
              className={cs.compressedNumericInput}
              fill={true}
              value={percentProps.percentOfProperty}
              onValueChange={(value) => {
                const clampedValue = Math.max(0, Math.min(100, value));
                dispatch({type: 'SET_THRESHOLD_PERCENTAGE', value: clampedValue});
              }}
              min={0}
              max={100}
              stepSize={1}
              minorStepSize={0.1}
              majorStepSize={10}
              rightElement={<B.Tag minimal>%</B.Tag>}
              disabled={disableInputs}
            />
          </B.FormGroup>
        )}

        {areaSizeOption === 'area' && (
          <B.FormGroup label={`Set a minimum area for detected changes`}>
            <B.NumericInput
              className={cs.compressedNumericInput}
              fill={true}
              value={minAlertAreaInput}
              stepSize={1}
              minorStepSize={1}
              majorStepSize={10}
              onValueChange={(_, valueAsString) => {
                if (valueAsString.trim() === '') {
                  setMinAlertAreaInput('');
                  dispatch({type: 'SET_MINIMUM_ALERTING_AREA', value: null});
                  return;
                }
                setMinAlertAreaInput(valueAsString);
                const typed = Math.max(0, Number(valueAsString));
                const factor = conversionUtils.getConversion(C.UNIT_AREA_M2, displayAreaUnit);
                const valueM2 = typed / factor;
                dispatch({type: 'SET_MINIMUM_ALERTING_AREA', value: valueM2});
              }}
              min={0}
              disabled={disableInputs}
              rightElement={<B.Tag minimal>{UNITS_UI[displayAreaUnit]}</B.Tag>}
            />
          </B.FormGroup>
        )}
      </div>
    );
  }
);
